import { useState, useEffect, useRef } from 'react';
import { DateTimePicker, DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { Box } from '@mui/material';
import { trTR } from '@mui/x-date-pickers/locales';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

// Dayjs plugins
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale('tr');

interface DateTimeInputFilterProps {
    between?: boolean;
    item: { value: string, label: string };
    applyValue: (item: { value: string }) => void;
    styleProps?: any;
    dateOnly?: boolean; // Yeni prop: sadece tarih seçimi için
    isEndDate?: boolean; // Bitiş tarihi için 23:59:59 saatini kullan
}

const DateTimeInputFilter = ({ between, item, applyValue, styleProps, dateOnly = false, isEndDate = false }: DateTimeInputFilterProps) => {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const justClearedRef = useRef(false);
    const parseValue = () => {
        if (!item.value) return null;
        const parsed = dayjs(item.value);
        return parsed.isValid() ? parsed : null;
    };
    const [localValue, setLocalValue] = useState<dayjs.Dayjs | null>(() => parseValue());
    const [clearKey, setClearKey] = useState(0);

    useEffect(() => {
        if (!item.value || item.value === '') {
            justClearedRef.current = false;
            setLocalValue(null);
            return;
        }
        if (justClearedRef.current) return;
        setLocalValue(parseValue());
    }, [item.value]);

    const handleDateChange = (newValue: dayjs.Dayjs | null) => {
        if (newValue && dayjs.isDayjs(newValue) && newValue.isValid()) {
            justClearedRef.current = false;
            setLocalValue(newValue);
            if (dateOnly) {
                if (isEndDate) {
                    applyValue({ ...item, value: newValue.endOf('day').format('YYYY-MM-DDTHH:mm:ss') });
                } else {
                    applyValue({ ...item, value: newValue.startOf('day').format('YYYY-MM-DDTHH:mm:ss') });
                }
            } else {
                applyValue({ ...item, value: newValue.format('YYYY-MM-DDTHH:mm:ss') });
            }
        } else if (newValue === null) {
            if (justClearedRef.current) return;
            justClearedRef.current = true;
            setLocalValue(null);
            setClearKey((k) => k + 1);
            applyValue({ ...item, value: '' });
        }
        // Geçersiz / yarım giriş (örn. yıl "202"): state ve filtre değiştirme, son hane girilebilsin
    };

    const handleBlur = () => {
        const raw = inputRef.current?.value ?? '';
        const isEmpty = !raw.trim();
        if (isEmpty) {
            if (justClearedRef.current) return;
            justClearedRef.current = true;
            setLocalValue(null);
            setClearKey((k) => k + 1);
            applyValue({ ...item, value: '' });
        }
    };

    const handleClear = () => {
        justClearedRef.current = true;
        setLocalValue(null);
        setClearKey((k) => k + 1);
        applyValue({ ...item, value: '' });
    };

    const baseStyles = {
        '& .MuiInputBase-root': {
            height: between ? '20px' : '25px',
            ...(between && {
                background: "#cdd8f3",
                zIndex: 50
            })
        },
        '& .MuiOutlinedInput-root': {
            '&:hover fieldset': {
                borderColor: '#90caf9',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#1976d2',
            }
        },
        '& .MuiInputBase-input': {
            padding: '4px 8px',
            cursor: 'pointer'
        }
    };

    const fieldSlotProps = {
        clearable: true,
        onClear: (e: React.MouseEvent) => {
            e.stopPropagation();
            handleClear();
        }
    };

    return (
        <LocalizationProvider
            dateAdapter={AdapterDayjs}
            adapterLocale="tr"
            localeText={trTR.components.MuiLocalizationProvider.defaultProps.localeText}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                {dateOnly ? (
                    <DatePicker
                        key={clearKey}
                        sx={baseStyles}
                        value={localValue}
                        onChange={handleDateChange}
                        format="DD.MM.YYYY"
                        slotProps={{
                            field: fieldSlotProps,
                            textField: {
                                size: "small",
                                variant: "outlined",
                                fullWidth: true,
                                inputRef: inputRef,
                                onBlur: handleBlur,
                                InputProps: {
                                    style: { cursor: 'pointer' }
                                }
                            },
                            popper: {
                                style: { zIndex: 1400 }
                            },
                            day: {
                                sx: {
                                    fontSize: '0.95rem',
                                    fontWeight: 500
                                }
                            },
                            calendarHeader: {
                                sx: {
                                    '& .MuiPickersCalendarHeader-label': {
                                        fontSize: '1rem',
                                        fontWeight: 600
                                    }
                                }
                            }
                        }}
                        views={['year', 'month', 'day']}
                    />
                ) : (
                    <DateTimePicker
                        key={clearKey}
                        sx={baseStyles}
                        value={localValue}
                        onChange={handleDateChange}
                        format="DD.MM.YYYY HH:mm"
                        slotProps={{
                            field: fieldSlotProps,
                            textField: {
                                size: "small",
                                variant: "outlined",
                                fullWidth: true,
                                inputRef: inputRef,
                                onBlur: handleBlur,
                                InputProps: {
                                    style: { cursor: 'pointer' }
                                }
                            },
                            popper: {
                                style: { zIndex: 1400 }
                            },
                            day: {
                                sx: {
                                    fontSize: '0.95rem',
                                    fontWeight: 500
                                }
                            },
                            calendarHeader: {
                                sx: {
                                    '& .MuiPickersCalendarHeader-label': {
                                        fontSize: '1rem',
                                        fontWeight: 600
                                    }
                                }
                            }
                        }}
                        views={['year', 'month', 'day', 'hours', 'minutes']}
                        timeSteps={{ minutes: 1 }}
                    />
                )}
            </Box>
        </LocalizationProvider>
    );
};

export default DateTimeInputFilter;